/**
 * Growth projection — fractional headcount, phased rates, optional Monte Carlo.
 */

import { authOnceCogs, profilePnL, plaidCogsEffective } from './engine';

function roundMoney(n) {
  return Math.round(n * 100) / 100;
}

export const DEFAULT_GROWTH_PHASES = [
  {
    throughMonth: 6,
    monthlyGrowthPct: 22,
    volatilityPct: 40,
    label: 'Early (pre-PMF)',
  },
  {
    throughMonth: 18,
    monthlyGrowthPct: 10,
    volatilityPct: 18,
    label: 'Middle (traction)',
  },
  {
    throughMonth: 36,
    monthlyGrowthPct: 5,
    volatilityPct: 10,
    label: 'Late (scale)',
  },
];

export const DEFAULT_GROWTH_MODEL = {
  model: 'monte_carlo',
  distribution: 'lognormal',
  mcRuns: 500,
  mcSeed: 20260811,
};

export function mergeProfileGrowth(profileGrowth, profiles) {
  return profiles.map((profile) => {
    const existing = (profileGrowth || []).find((row) => row.profileId === profile.id);
    return (
      existing
        ? {
            ...existing,
            share0: Math.max(0, Number(existing.share0) || 0),
            monthlyGrowthPct: Math.min(
              1000,
              Math.max(-95, Number(existing.monthlyGrowthPct) || 0)
            ),
          }
        : {
            profileId: profile.id,
            share0: 0,
            monthlyGrowthPct: 0,
          }
    );
  });
}

/** True when paying headcount exists but no profile has a positive start share. */
export function isMixUnallocated(growth) {
  const startPaying = Math.max(0, Number(growth?.startPaying) || 0);
  const shares = (growth?.profileGrowth || []).reduce(
    (s, r) => s + Math.max(0, Number(r.share0) || 0),
    0
  );
  return startPaying > 0 && shares <= 0;
}

function normalizeShares(profileGrowth) {
  const normalizedInput = profileGrowth.map((row) => ({
    ...row,
    share0: Math.max(0, Number(row.share0) || 0),
  }));
  const total = normalizedInput.reduce((sum, row) => sum + row.share0, 0);
  if (total <= 0) {
    return normalizedInput.map((row) => ({ ...row, share0: 0 }));
  }
  return normalizedInput.map((row) => ({
    ...row,
    share0: row.share0 / total,
  }));
}

/** @param {object} growth @param {number} monthIndex — 1-based month after Now */
export function resolvePhase(growth, monthIndex) {
  const phases = (growth?.phases?.length > 0 ? growth.phases : DEFAULT_GROWTH_PHASES)
    .map((phase, index) => ({
      ...phase,
      throughMonth: Math.max(1, Number(phase.throughMonth) || 1),
      monthlyGrowthPct: Math.min(
        1000,
        Math.max(-95, Number(phase.monthlyGrowthPct) || 0)
      ),
      volatilityPct: Math.max(0, Number(phase.volatilityPct) || 0),
      label: phase.label || `Phase ${index + 1}`,
    }))
    .sort((a, b) => a.throughMonth - b.throughMonth);
  return phases.find((p) => monthIndex <= p.throughMonth) || phases[phases.length - 1];
}

function createRng(seed) {
  let state = seed >>> 0;
  return () => {
    state += 0x6d2b79f5;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function gaussian(rng) {
  let u = Number(rng());
  let v = Number(rng());
  if (!(u > 0)) u = Number.MIN_VALUE;
  if (!(v > 0)) v = Number.MIN_VALUE;
  u = Math.min(1, u);
  v = Math.min(1, v);
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

/**
 * Sample monthly pool growth rate (decimal, e.g. 0.1 = 10%).
 * @param {object} phase
 * @param {'fixed'|'normal'|'lognormal'} distribution
 * @param {(() => number)|null} rng
 */
export function sampleMonthlyGrowthRate(phase, distribution, rng) {
  const rawMean = Number(phase?.monthlyGrowthPct);
  const rawVol = Number(phase?.volatilityPct);
  const mean = Math.min(10, Math.max(-0.95, (Number.isFinite(rawMean) ? rawMean : 0) / 100));
  const vol = Math.min(10, Math.max(0, (Number.isFinite(rawVol) ? rawVol : 0) / 100));

  if (!rng || distribution === 'fixed' || vol <= 0) {
    return mean;
  }

  if (distribution === 'normal') {
    return Math.max(-0.95, mean + vol * gaussian(rng));
  }

  // lognormal on (1 + growth) — mean-corrected so E[g] ≈ monthlyGrowthPct/100
  const sigma = Math.max(0.01, vol);
  const meanFactor = Math.max(1 + mean, 0.001);
  const mu = Math.log(meanFactor) - (sigma * sigma) / 2;
  return Math.exp(mu + sigma * gaussian(rng)) - 1;
}

function allocateFractionalCounts(totalPaying, profileGrowth) {
  const normalized = normalizeShares(profileGrowth);
  const shareTotal = normalized.reduce((sum, row) => sum + row.share0, 0);
  const counts = {};
  if (shareTotal <= 0) {
    normalized.forEach((row) => {
      counts[row.profileId] = 0;
    });
    return counts;
  }
  let assigned = 0;
  normalized.forEach((row, index) => {
    if (index === normalized.length - 1) {
      counts[row.profileId] = Math.max(0, totalPaying - assigned);
    } else {
      const n = totalPaying * row.share0;
      counts[row.profileId] = n;
      assigned += n;
    }
  });
  return counts;
}

function evolveMixWeights(weights, growthRows) {
  const next = {};
  for (const row of growthRows) {
    const prev = weights[row.profileId] ?? row.share0 ?? 0;
    const mixShift = Math.max(0, 1 + (Number(row.monthlyGrowthPct) || 0) / 100);
    next[row.profileId] = prev * mixShift;
  }
  const total = Object.values(next).reduce((s, n) => s + n, 0);
  if (total <= 0) return weights;
  return Object.fromEntries(Object.entries(next).map(([k, v]) => [k, v / total]));
}

function buildMonthRow(
  m,
  profileById,
  growthRows,
  mixWeights,
  counts,
  rates,
  plans,
  plaidMode,
  newPaying = 0,
  newProfileCounts = {},
  unallocatedPaying = 0
) {
  let revenue = 0;
  let planRevenue = 0;
  let aiPackRevenue = 0;
  let plaidCogsTotal = 0;
  let stripeFees = 0;
  let aiCogsTotal = 0;
  let authOnceCogsTotal = 0;
  let items = 0;
  let paying = 0;
  const mix = [];

  for (const row of growthRows) {
    const profile = profileById[row.profileId];
    if (!profile) continue;
    const n = counts[row.profileId] || 0;
    if (n <= 0) continue;
    const pnl = profilePnL(profile, plans, rates);
    revenue += n * pnl.revenue;
    planRevenue += n * (pnl.planRevenue ?? pnl.revenue);
    aiPackRevenue += n * (pnl.aiPackRevenue || 0);
    plaidCogsTotal += n * pnl.cogs;
    stripeFees += n * pnl.stripe;
    aiCogsTotal += n * (pnl.ai || 0);
    items += n * (pnl.linkedItems ?? profile.items);
    paying += n;
    mix.push({
      profileId: row.profileId,
      plan: profile.plan,
      count: n,
    });
  }

  mix.forEach((row) => {
    row.share = paying > 0 ? row.count / paying : 0;
  });

  if (paying <= 0 && unallocatedPaying > 0) {
    paying = unallocatedPaying;
  }

  if (newPaying > 0) {
    const acquisitionCounts =
      Object.keys(newProfileCounts).length > 0
        ? Object.entries(newProfileCounts)
        : mix.map((row) => [row.profileId, newPaying * row.share]);
    for (const [profileId, count] of acquisitionCounts) {
      const profile = profileById[profileId];
      if (!profile || count <= 0) continue;
      authOnceCogsTotal +=
        count * authOnceCogs(profile, rates, plans[profile.plan] || {});
    }
  }

  revenue = roundMoney(revenue);
  planRevenue = roundMoney(planRevenue);
  aiPackRevenue = roundMoney(aiPackRevenue);
  plaidCogsTotal = roundMoney(plaidCogsTotal);
  const plaidCogsPayg = plaidCogsTotal;
  const plaidCogs = plaidCogsEffective(plaidCogsPayg, plaidMode);
  stripeFees = roundMoney(stripeFees);
  aiCogsTotal = roundMoney(aiCogsTotal);
  authOnceCogsTotal = roundMoney(authOnceCogsTotal);
  const profit = roundMoney(
    revenue - plaidCogs - stripeFees - aiCogsTotal - authOnceCogsTotal
  );
  const margin = roundMoney(revenue - plaidCogs - aiCogsTotal - authOnceCogsTotal);
  const marginPct = revenue > 0 ? margin / revenue : 0;
  const profitPct = revenue > 0 ? profit / revenue : 0;

  return {
    label: m === 0 ? 'Now' : `M${m}`,
    monthIndex: m,
    paying: Math.round(paying * 10) / 10,
    payingExact: paying,
    items: Math.round(items * 10) / 10,
    revenue,
    planRevenue,
    aiPackRevenue,
    authOnceCogs: authOnceCogsTotal,
    plaidCogsPayg,
    plaidCogs,
    stripeFees,
    aiCogs: aiCogsTotal,
    profit,
    margin,
    marginPct,
    profitPct,
    mix,
    mixWeights: { ...mixWeights },
  };
}

/**
 * Single growth path. Uses fractional headcount for P&L (no integer rounding plateaus).
 * @param {object} scenario
 * @param {(() => number)|null} rng — null = deterministic (fixed distribution)
 */
export function projectGrowthPath(scenario, rng = null) {
  const { rates, plans, profiles, growth, plaidMode = 'payg' } = scenario;
  const profileById = Object.fromEntries(profiles.map((p) => [p.id, p]));
  const growthRows = mergeProfileGrowth(growth.profileGrowth || [], profiles);
  const unallocatedPaying = isMixUnallocated(growth)
    ? Math.max(0, Number(growth.startPaying) || 0)
    : 0;
  const horizon = Math.min(
    36,
    Math.max(1, Math.floor(Number(growth.horizonMonths) || 12))
  );
  const churnFactor = Math.max(
    0,
    Math.min(1, 1 - Math.max(0, Number(growth.churnPct) || 0) / 100)
  );
  const distribution = growth.distribution || 'lognormal';
  const growthMode = growth.growthMode || 'profile';

  let profileCounts = allocateFractionalCounts(
    Math.max(0, Number(growth.startPaying) || 0),
    growthRows
  );

  const months = [];

  for (let m = 0; m < horizon; m += 1) {
    let newProfileCounts = {};
    if (m > 0) {
      const phase = resolvePhase(growth, m);
      if (growthMode === 'phased') {
        const totalPaying = Object.values(profileCounts).reduce((sum, n) => sum + n, 0);
        const g = sampleMonthlyGrowthRate(phase, distribution, rng);
        const nextTotal = Math.max(0, totalPaying * (1 + g) * churnFactor);
        const priorWeights = Object.fromEntries(
          Object.entries(profileCounts).map(([profileId, count]) => [
            profileId,
            totalPaying > 0 ? count / totalPaying : 0,
          ])
        );
        const nextWeights = evolveMixWeights(priorWeights, growthRows);
        const weightedGrowthRows = [];
        for (const row of growthRows) {
          weightedGrowthRows.push({
            ...row,
            share0: nextWeights[row.profileId] ?? 0,
          });
        }
        const nextCounts = allocateFractionalCounts(nextTotal, weightedGrowthRows);
        // Mix drift reallocates the existing pool; it is not new customer
        // acquisition. Auth is charged for gross acquisitions before churn,
        // even when churn makes the ending pool smaller.
        const grossNewPaying = totalPaying * Math.max(0, g);
        const newCounts = allocateFractionalCounts(grossNewPaying, weightedGrowthRows);
        Object.keys(newCounts).forEach((profileId) => {
          newProfileCounts[profileId] = newCounts[profileId];
        });
        profileCounts = nextCounts;
      } else {
        const nextCounts = {};
        for (const row of growthRows) {
          const current = Math.max(0, profileCounts[row.profileId] || 0);
          const profileRate = sampleMonthlyGrowthRate(
            {
              monthlyGrowthPct: row.monthlyGrowthPct,
              volatilityPct: phase.volatilityPct,
            },
            distribution,
            rng
          );
          nextCounts[row.profileId] = Math.max(0, current * (1 + profileRate) * churnFactor);
          // Churn can make ending headcount contract while positive growth
          // still represents gross acquisitions that incur one-time Auth.
          newProfileCounts[row.profileId] = current * Math.max(0, profileRate);
        }
        profileCounts = nextCounts;
      }
    }

    const totalPaying = Object.values(profileCounts).reduce((sum, n) => sum + n, 0);
    const mixWeights = Object.fromEntries(
      Object.entries(profileCounts).map(([profileId, count]) => [
        profileId,
        totalPaying > 0 ? count / totalPaying : 0,
      ])
    );
    months.push(
      buildMonthRow(
        m,
        profileById,
        growthRows,
        mixWeights,
        profileCounts,
        rates,
        plans,
        plaidMode,
        Object.values(newProfileCounts).reduce((sum, n) => sum + n, 0),
        newProfileCounts,
        unallocatedPaying
      )
    );
  }

  return months;
}

function percentile(sorted, p) {
  if (!sorted.length) return 0;
  const idx = (sorted.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
}

function summarizeBand(values) {
  const sorted = [...values].sort((a, b) => a - b);
  return {
    p10: roundMoney(percentile(sorted, 0.1)),
    p50: roundMoney(percentile(sorted, 0.5)),
    p90: roundMoney(percentile(sorted, 0.9)),
  };
}

/**
 * @param {object} scenario
 * @returns {{ months: object[], mc: boolean, runs?: number, distribution?: string }}
 */
export function projectGrowthMonteCarlo(scenario) {
  const runs = Math.min(
    5000,
    Math.max(50, Math.floor(Number(scenario.growth?.mcRuns) || 500))
  );
  const horizon = Math.min(
    36,
    Math.max(1, Math.floor(Number(scenario.growth?.horizonMonths) || 12))
  );
  const parsedSeed = Number(scenario.growth?.mcSeed);
  const seed = Number.isFinite(parsedSeed) ? Math.floor(parsedSeed) : 20260811;
  const buckets = Array.from({ length: horizon }, () => ({
    revenue: [],
    planRevenue: [],
    profit: [],
    profitPct: [],
    marginPct: [],
    paying: [],
    items: [],
    plaidCogs: [],
    plaidCogsPayg: [],
    stripeFees: [],
    authOnceCogs: [],
    aiCogs: [],
    aiPackRevenue: [],
  }));
  const mixBuckets = Array.from({ length: horizon }, () => new Map());

  for (let r = 0; r < runs; r += 1) {
    const rng = createRng(seed + r * 9973);
    const path = projectGrowthPath(scenario, rng);
    path.forEach((month, i) => {
      buckets[i].revenue.push(month.revenue);
      buckets[i].planRevenue.push(month.planRevenue);
      buckets[i].profit.push(month.profit);
      buckets[i].profitPct.push(month.profitPct);
      buckets[i].marginPct.push(month.marginPct);
      buckets[i].paying.push(month.payingExact);
      buckets[i].items.push(month.items);
      buckets[i].plaidCogs.push(month.plaidCogs);
      buckets[i].plaidCogsPayg.push(month.plaidCogsPayg);
      buckets[i].stripeFees.push(month.stripeFees);
      buckets[i].authOnceCogs.push(month.authOnceCogs);
      buckets[i].aiCogs.push(month.aiCogs);
      buckets[i].aiPackRevenue.push(month.aiPackRevenue);
      for (const row of month.mix || []) {
        const existing = mixBuckets[i].get(row.profileId) || {
          profileId: row.profileId,
          plan: row.plan,
          count: 0,
        };
        existing.count += row.count || 0;
        mixBuckets[i].set(row.profileId, existing);
      }
    });
  }

  const months = buckets.map((bucket, i) => {
    const rev = summarizeBand(bucket.revenue);
    const planRev = summarizeBand(bucket.planRevenue);
    const prof = summarizeBand(bucket.profit);
    const profitPct = summarizeBand(bucket.profitPct);
    const marginPct = summarizeBand(bucket.marginPct);
    const pay = summarizeBand(bucket.paying);
    const itemCount = summarizeBand(bucket.items);
    const cogs = summarizeBand(bucket.plaidCogs);
    const cogsPayg = summarizeBand(bucket.plaidCogsPayg);
    const stripe = summarizeBand(bucket.stripeFees);
    const auth = summarizeBand(bucket.authOnceCogs);
    const ai = summarizeBand(bucket.aiCogs);
    const aiPacks = summarizeBand(bucket.aiPackRevenue);
    const averagePaying =
      bucket.paying.reduce((sum, value) => sum + value, 0) / Math.max(runs, 1);
    const mix = [...mixBuckets[i].values()]
      .map((row) => ({
        ...row,
        count: row.count / Math.max(runs, 1),
      }))
      .filter((row) => row.count > 0)
      .map((row) => ({
        ...row,
        share: averagePaying > 0 ? row.count / averagePaying : 0,
      }));
    const mixWeights = Object.fromEntries(
      mix.map((row) => [row.profileId, row.share])
    );

    return {
      label: i === 0 ? 'Now' : `M${i}`,
      monthIndex: i,
      mc: true,
      revenue: rev.p50,
      revenueP10: rev.p10,
      revenueP50: rev.p50,
      revenueP90: rev.p90,
      planRevenue: planRev.p50,
      planRevenueP10: planRev.p10,
      planRevenueP50: planRev.p50,
      planRevenueP90: planRev.p90,
      profit: prof.p50,
      profitP10: prof.p10,
      profitP50: prof.p50,
      profitP90: prof.p90,
      paying: pay.p50,
      payingP10: pay.p10,
      payingP50: pay.p50,
      payingP90: pay.p90,
      items: itemCount.p50,
      itemsP10: itemCount.p10,
      itemsP50: itemCount.p50,
      itemsP90: itemCount.p90,
      plaidCogsPaygP10: cogsPayg.p10,
      plaidCogsPaygP50: cogsPayg.p50,
      plaidCogsPaygP90: cogsPayg.p90,
      plaidCogsPayg: cogsPayg.p50,
      plaidCogs: cogs.p50,
      plaidCogsP10: cogs.p10,
      plaidCogsP50: cogs.p50,
      plaidCogsP90: cogs.p90,
      stripeFees: stripe.p50,
      stripeFeesP10: stripe.p10,
      stripeFeesP50: stripe.p50,
      stripeFeesP90: stripe.p90,
      aiCogs: ai.p50,
      aiCogsP10: ai.p10,
      aiCogsP50: ai.p50,
      aiCogsP90: ai.p90,
      aiPackRevenue: aiPacks.p50,
      aiPackRevenueP10: aiPacks.p10,
      aiPackRevenueP50: aiPacks.p50,
      aiPackRevenueP90: aiPacks.p90,
      authOnceCogs: auth.p50,
      authOnceCogsP10: auth.p10,
      authOnceCogsP50: auth.p50,
      authOnceCogsP90: auth.p90,
      profitPct: profitPct.p50,
      profitPctP10: profitPct.p10,
      profitPctP50: profitPct.p50,
      profitPctP90: profitPct.p90,
      marginPct: marginPct.p50,
      marginPctP10: marginPct.p10,
      marginPctP50: marginPct.p50,
      marginPctP90: marginPct.p90,
      mix,
      mixWeights,
    };
  });

  return {
    months,
    mc: true,
    runs,
    distribution: scenario.growth?.distribution || 'lognormal',
  };
}

/**
 * @param {object} scenario
 */
export function projectGrowth(scenario) {
  const model = scenario.growth?.model || 'monte_carlo';
  if (model === 'monte_carlo') {
    return projectGrowthMonteCarlo(scenario);
  }
  return {
    months: projectGrowthPath(scenario, null),
    mc: false,
    distribution: 'fixed',
  };
}
